/**
 * Existing Vehicle Lookup
 *
 * Lightweight Firestore lookup to check if a vehicle with
 * the same license plate already exists in the team's inventory.
 *
 * This is a non-blocking, fire-once lookup that:
 * - Queries by plateNormalized
 * - Excludes soft-deleted vehicles (deletedAt != null)
 * - Returns minimal data (id + displayName)
 * - Swallows errors silently
 */

import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { normalizePlate } from '@/lib/rdwClient';
import type { ExistingVehicleResult } from './types';

/**
 * Lookup an existing vehicle by normalized license plate
 *
 * @param teamId - The team ID to search within
 * @param plate - The license plate to search for (will be normalized)
 * @returns ExistingVehicleResult - vehicle info, null if not found, 'UNKNOWN' on error
 */
export async function lookupExistingVehicle(
  teamId: string,
  plate: string
): Promise<ExistingVehicleResult> {
  // Validate inputs
  if (!teamId || !plate) {
    return null;
  }

  const normalized = normalizePlate(plate);
  if (normalized.length !== 6) {
    return null;
  }

  try {
    // Query for non-deleted vehicles with matching plate
    const q = query(
      collection(db, 'teams', teamId, 'vehicles'),
      where('plateNormalized', '==', normalized),
      where('deletedAt', '==', null),
      limit(1)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    // Build display name from available fields
    const displayName =
      data.displayName ||
      [data.brand, data.model].filter(Boolean).join(' ') ||
      data.licensePlate ||
      normalized;

    return {
      id: doc.id,
      displayName,
    };
  } catch {
    // Swallow errors silently - return UNKNOWN
    return 'UNKNOWN';
  }
}

/**
 * Lookup with timeout protection
 * If lookup takes longer than timeout, returns null (not UNKNOWN)
 * to avoid blocking the UI
 *
 * @param teamId - The team ID to search within
 * @param plate - The license plate to search for
 * @param timeoutMs - Timeout in milliseconds (default 3000ms)
 */
export async function lookupExistingVehicleWithTimeout(
  teamId: string,
  plate: string,
  timeoutMs: number = 3000
): Promise<ExistingVehicleResult> {
  const lookupPromise = lookupExistingVehicle(teamId, plate);

  const timeoutPromise = new Promise<ExistingVehicleResult>((resolve) => {
    setTimeout(() => resolve(null), timeoutMs);
  });

  return Promise.race([lookupPromise, timeoutPromise]);
}
